import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Page, Header, HeaderCard, HeaderImage, Section, SectionTitle, Accordion, AccordionItem, AccordionHeader, AccordionContent, FormCard, Button } from './FoodPremisesStyles';
import { FaChevronDown, FaChevronUp, FaFilePdf, FaCheckCircle, FaTimesCircle, FaClock, FaDownload } from 'react-icons/fa';
import { Oval } from 'react-loader-spinner';
import FoodPremisesApplicationForm from '../components/FoodPremisesApplicationForm';

const FoodPremises = () => {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [existingApp, setExistingApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successData, setSuccessData] = useState(null);

  // Accordion states
  const [expandedLaws, setExpandedLaws] = useState({});
  const [expandedFees, setExpandedFees] = useState({});

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [contentData, appData] = await Promise.all([
          api.foodPremises.getContent(),
          user ? api.foodPremises.getMyApplication().catch(() => null) : Promise.resolve(null)
        ]);
        setContent(contentData);
        setExistingApp(appData);
      } catch (error) {
        console.error('Failed to load data', error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return '#16a34a';
      case 'rejected': return '#dc2626';
      default: return '#ca8a04';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <FaCheckCircle />;
      case 'rejected': return <FaTimesCircle />;
      default: return <FaClock />;
    }
  };

  const toggleLaw = (index) => {
    setExpandedLaws(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleFee = (index) => {
    setExpandedFees(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleFormSuccess = (response) => {
    if (user) {
      setExistingApp(response);
    } else {
      setSuccessData(response);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Oval color="#000" height={40} width={40} /></div>;

  if (existingApp) {
    return (
      <Page>
        <Header>
          <HeaderCard>
            <h1>Application Status</h1>
            <p>Welcome, {existingApp.businessOwnerName}</p>
          </HeaderCard>
        </Header>

        <Section>
          <FormCard>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
              <div style={{ fontSize: 32, color: getStatusColor(existingApp.status) }}>
                {getStatusIcon(existingApp.status)}
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 24 }}>Status: <span style={{ textTransform: 'capitalize', color: getStatusColor(existingApp.status) }}>{existingApp.status}</span></h2>
                <p style={{ margin: '4px 0 0', color: '#666' }}>Submitted on {new Date(existingApp.createdAt).toLocaleDateString()}</p>
              </div>
            </div>

            {existingApp.status === 'approved' && existingApp.certificate && (
              <div style={{ marginBottom: 32, padding: 24, background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                <h3 style={{ marginTop: 0, color: '#16a34a' }}>Certificate Issued</h3>
                <p>Your food premises certificate is available for download.</p>
                <a href={existingApp.certificate} download="FAL_Certificate.png" style={{ textDecoration: 'none' }}>
                  <Button style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaDownload /> Download Certificate
                  </Button>
                </a>
              </div>
            )}

            <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: 8, marginBottom: 16 }}>Application Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div><strong>Premises Name:</strong> {existingApp.premisesName}</div>
              <div><strong>Physical Address:</strong> {existingApp.physicalAddress}</div>
              <div><strong>Contact:</strong> {existingApp.contactNumbers}</div>
              <div><strong>Email:</strong> {existingApp.email}</div>
              <div><strong>Type:</strong> {existingApp.typeOfPremises?.join(', ')}</div>
              <div><strong>Registration No:</strong> {existingApp.businessRegistrationNumber}</div>
              <div><strong>Employees:</strong> {existingApp.numberOfEmployees}</div>
            </div>
            
            <div style={{ marginTop: 24 }}>
               <Link to="/business-dashboard">Go to Business Dashboard</Link>
            </div>
          </FormCard>
        </Section>
      </Page>
    );
  }

  if (successData) {
    return (
      <Page>
        <Section>
          <FormCard style={{ textAlign: 'center', padding: 60 }}>
            <h2 style={{ color: '#16a34a', fontSize: 32, marginBottom: 16 }}>Application Submitted!</h2>
            <p style={{ fontSize: 18, marginBottom: 24 }}>
              Your food premises application has been successfully received.
            </p>
            <div style={{ background: '#f0fdf4', padding: 24, borderRadius: 12, border: '1px solid #bbf7d0', display: 'inline-block', textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 12px' }}>Account Credentials</h3>
              <p>Your account has been automatically created. You can use these credentials to log in and track your application status.</p>
              <p><strong>Username:</strong> {successData.credentials.username}</p>
              <p><strong>Password:</strong> {successData.credentials.password}</p>
            </div>
            <div style={{ marginTop: 32 }}>
              <Button onClick={() => window.location.href = '/admin/login'}>Go to Login</Button>
            </div>
          </FormCard>
        </Section>
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <Header>
        <HeaderCard>
          <h1>{content?.header?.title || 'Food Premises'}</h1>
          <p>{content?.header?.description || 'Welcome to the Food Premises section.'}</p>
        </HeaderCard>
        <HeaderImage>
          {content?.header?.image && <img src={content.header.image} alt="Header" />}
        </HeaderImage>
      </Header>

      {/* Food Laws */}
      <Section>
        <SectionTitle>Comprehensive Explanation of Food Laws</SectionTitle>
        <Accordion>
          {content?.foodLaws?.map((law, index) => (
            <AccordionItem key={index}>
              <AccordionHeader onClick={() => toggleLaw(index)} $expanded={expandedLaws[index]}>
                <span>{law.title}</span>
                {expandedLaws[index] ? <FaChevronUp /> : <FaChevronDown />}
              </AccordionHeader>
              {expandedLaws[index] && (
                <AccordionContent>
                  <div dangerouslySetInnerHTML={{ __html: law.content }} />
                  {law.pdfFile && (
                    <div style={{ marginTop: 12 }}>
                      <a href={law.pdfFile} download={`${law.title}.pdf`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaFilePdf /> Download Full Document
                      </a>
                    </div>
                  )}
                </AccordionContent>
              )}
            </AccordionItem>
          ))}
          {(!content?.foodLaws || content.foodLaws.length === 0) && <p>No information available yet.</p>}
        </Accordion>
      </Section>

      {/* Fee Schedule */}
      <Section>
        <SectionTitle>Fee Schedule</SectionTitle>
        <Accordion>
          {content?.feeSchedule?.map((item, index) => (
            <AccordionItem key={index}>
              <AccordionHeader onClick={() => toggleFee(index)} $expanded={expandedFees[index]}>
                <span>{item.title}</span>
                {expandedFees[index] ? <FaChevronUp /> : <FaChevronDown />}
              </AccordionHeader>
              {expandedFees[index] && (
                <AccordionContent>
                  <div dangerouslySetInnerHTML={{ __html: item.content }} />
                  {item.pdfFile && (
                    <div style={{ marginTop: 12 }}>
                      <a href={item.pdfFile} download={`${item.title}.pdf`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaFilePdf /> Download Fee Schedule
                      </a>
                    </div>
                  )}
                </AccordionContent>
              )}
            </AccordionItem>
          ))}
           {(!content?.feeSchedule || content.feeSchedule.length === 0) && <p>No fee information available yet.</p>}
        </Accordion>
      </Section>

      {/* Application Form */}
      <Section>
        <SectionTitle>Food Premises Listing Form</SectionTitle>
        <FoodPremisesApplicationForm onSuccess={handleFormSuccess} user={user} />
      </Section>
    </Page>
  );
};

export default FoodPremises;
